import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function DowntonAbbeyGrandFinalePageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Downton Abbey: The Grand Finale Movie Release' }
  ];

  const article = {
    category: 'Event',
    date: 'September 12, 2025',
    title: 'Downton Abbey: The Grand Finale Movie Release',
    subtitle: 'Global Attention as Culmination of Butler Culture',
    heroImage: '/images/generated/jba-fs-068-news_downton-abbey-grand-finale.jpg',
    lead: 'The final film of the globally beloved series grosses $46 million. Legendary butler Mr. Carson returns in this moving portrayal of butler tradition and pride, representing the culmination of butler culture.',
    comment: 'The global success of "Downton Abbey: The Grand Finale" once again proves the universal value and appeal of the butler profession, which our association finds very gratifying. The expertise, loyalty, and above all "spirit of omotenashi" of butlers portrayed through the character Mr. Carson is precisely the ideal image of butler service we aim for. We expect that as this film continues to be loved worldwide, understanding and respect for the butler profession will deepen, greatly influencing the development of butler culture in Japan. We will convey the true value of butlers shown in this work to people in Japan and strive to provide higher quality butler services.',
    related: [
      { to: '/en/news/apple-robot-butler', image: '/images/generated/jba-fs-072-news_downton-abbey-grand-finale-v3.jpg', aspect: 'aspect-[4/3]', alt: 'Related article: Apple Robot Butler Development Project', title: 'Apple Robot Butler Development Project', desc: 'The future of butler service opened by AI technology' },
      { to: '/en/news/raffles-london-campaign', image: '/images/generated/jba-fs-073-news_downton-abbey-grand-finale-v3.jpg', aspect: 'aspect-[3/4]', alt: 'Related article: Raffles London "The Butler Did It" Campaign Chapter 2', title: 'Raffles London "The Butler Did It" Campaign Chapter 2', desc: 'Luxury hotel butler service innovation' }
    ]
  };

  const sources = [
    { label: 'Overview & Production', value: 'https://en.wikipedia.org/wiki/Downton_Abbey:_The_Grand_Finale', link: true },
    { label: 'Box Office Data', value: 'https://www.boxofficemojo.com/release/rl1014267905/', link: true },
    { label: 'Critics & Audience', value: 'https://www.rottentomatoes.com/m/downton_abbey_the_grand_finale', link: true },
    { label: 'Official Site', value: 'https://www.focusfeatures.com/downton-abbey-the-grand-finale', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];

  const toc = [
    { id: 's-success', label: 'Film Success and Box Office Revenue' },
    { id: 's-production', label: 'Production and Background' },
    { id: 's-boxoffice', label: 'Box Office Figures' },
    { id: 's-reception', label: 'Critical and Audience Reception' },
    { id: 's-impact', label: 'Impact and Significance on Butler Culture' },
    { id: 's-history', label: 'The Series Journey' },
    { id: 's-global', label: 'International Response and Cultural Value' }
  ];

  return (
    <Layout>
      <title>Downton Abbey: The Grand Finale Movie Release | Japan Butler Association</title>
      <meta name="description" content="The final film of the globally beloved series grosses $46 million, with legendary butler Mr. Carson returning in this moving portrayal of butler tradition and pride." />
      <link rel="canonical" href="https://butler-association.jp/en/news/downton-abbey-grand-finale" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/downton-abbey-grand-finale" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/downton-abbey-grand-finale" />

      <ArticleHero
        eyebrow="News / Event"
        category={article.category}
        title={article.title}
        subtitle={article.subtitle}
        date={article.date}
        image={article.heroImage}
      />
      {/* Breadcrumb directly after billboard */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Lead (full-width allowed here only) */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      {/* Body (single editorial column) + sticky sidebar (lg+) */}
      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'The trilogy finale opened in the U.S. on September 12, 2025, grossing roughly $103.5 million worldwide',
                'Legendary butler Mr. Carson (played by Jim Carter) returns, placing the tradition and pride of the butler profession at the heart of the story',
                'A 92% critics score (Certified Fresh) on Rotten Tomatoes and the grand conclusion of a 15-year saga drew wide acclaim'
              ]}
            />

            {/* 01: Film Success and Box Office Revenue (existing body, unchanged) */}
            <SectionHeading id="s-success" en="Box Office">Film Success and Box Office Revenue</SectionHeading>
            <div className="space-y-6">
              <p className={P}>"Downton Abbey: The Grand Finale" recorded $46 million in box office revenue during its opening weekend, achieving global success. This film, as the long-awaited sequel to the 2019 first film, far exceeded fan expectations.</p>
              <p className={P}>Particularly noteworthy is the return of legendary butler Mr. Carson (played by Jim Carter). His presence symbolizes the tradition and pride of the butler profession, giving deep meaning to the entire film.</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-focusfeatures.jpg"
              alt="Screenshot of the official Focus Features website page introducing the film Downton Abbey: The Grand Finale"
              media="Focus Features (official site)"
              url="https://www.focusfeatures.com/downton-abbey-the-grand-finale"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="Legendary butler Mr. Carson returns in this moving portrayal of butler tradition and pride." cite="Downton Abbey: The Grand Finale" />
            </div>

            {/* 02 (augmented): Production and Background */}
            <SectionHeading id="s-production" en="Production">Production and Background</SectionHeading>
            <div className="space-y-6">
              <p className={P}>"Downton Abbey: The Grand Finale" is a 2025 historical drama film written by Julian Fellowes, who has shaped the series throughout, and directed by Simon Curtis. It is the third and final film of the trilogy, following 2022's "A New Era," and was made on a net budget of about <Stat>$50 million</Stat>. With a running time of 2 hours 3 minutes and a PG rating, it was distributed by Focus Features, which has handled the franchise since the first film in 2019.</p>
              <p className={P}>The cast reunites leading actors from the television series and the previous two films, including Hugh Bonneville, Michelle Dockery, Elizabeth McGovern and Paul Giamatti. Jim Carter also returns as butler Mr. Carson with his signature presence.</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-wikipedia.jpg"
              alt="Screenshot of the English Wikipedia page showing the director, screenwriter and budget details for Downton Abbey: The Grand Finale"
              media="Wikipedia (English)"
              url="https://en.wikipedia.org/wiki/Downton_Abbey:_The_Grand_Finale"
            />

            {/* 03 (augmented): Box Office Figures */}
            <SectionHeading id="s-boxoffice" en="Box Office Data">Box Office Figures</SectionHeading>
            <div className="space-y-6">
              <p className={P}>After release, the film grossed <Stat>$103.5M</Stat> worldwide, a commercial success crowning the finale of the series. This breaks down to roughly <Stat>$45.0M</Stat> in North America (U.S. and Canada) and about <Stat>$58.5M</Stat> in international markets, with international accounting for around 56.5% of the total and underscoring the series' global reach.</p>
              <p className={P}>Its U.S. opening weekend earned about $18.1 million across 3,694 theaters, with a widest release reaching 3,711 theaters. It was distributed by Focus Features and opened in the U.S. on September 12, 2025.</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-boxofficemojo.jpg"
              alt="Screenshot of the Box Office Mojo page showing the domestic, international and worldwide gross figures and theater counts for the film"
              media="Box Office Mojo"
              url="https://www.boxofficemojo.com/release/rl1014267905/"
            />

            {/* 04 (augmented): Critical and Audience Reception */}
            <SectionHeading id="s-reception" en="Reception">Critical and Audience Reception</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The film also earned strong reviews. On the review-aggregation site Rotten Tomatoes it holds a critics score of <Stat>92%</Stat> (142 reviews), earning a "Certified Fresh" seal. The audience rating (Popcornmeter) climbed even higher to <Stat>95%</Stat>, drawing firm support from fans who have loved the series for years.</p>
              <p className={P}>As the closing chapter of the trilogy, its careful portrayal of narrative closure and a fond farewell to the characters is cited as a key reason it won over both critics and audiences.</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-rottentomatoes.jpg"
              alt="Screenshot of the Rotten Tomatoes page showing the 92 percent critics score and 95 percent audience score for the film"
              media="Rotten Tomatoes"
              url="https://www.rottentomatoes.com/m/downton_abbey_the_grand_finale"
            />

            {/* 05: Impact and Significance on Butler Culture (existing body, unchanged) */}
            <SectionHeading id="s-impact" en="Cultural Impact">Impact and Significance on Butler Culture</SectionHeading>
            <div className="my-6">
              <KeyPointsBox
                title="What this film brought about"
                points={[
                  'Revived the butler profession in modern times and made its expertise and importance known worldwide',
                  'Portrayed butlers not as mere servants, but as essential professionals in household management',
                  'Drove concrete impacts such as increased enrollment applications at butler training schools worldwide'
                ]}
              />
            </div>
            <div className="space-y-6">
              <p className={P}>The Downton Abbey series is a groundbreaking work that revived the butler profession in modern times and made its expertise and importance known worldwide. Butler characters like Mr. Carson are portrayed not as mere servants, but as essential professionals in household management.</p>
              <p className={P}>The film's success reflects growing interest in butler services in modern society, with concrete impacts such as increased enrollment applications at butler training schools worldwide.</p>
            </div>

            {/* 06 (augmented): The Series Journey */}
            <SectionHeading id="s-history" en="The Journey">The Series Journey</SectionHeading>
            <div className="space-y-6">
              <p className={P}>"Downton Abbey" began in 2010 as a television drama on Britain's ITV, winning worldwide popularity and running for six seasons through 2015. A first feature film followed in 2019, and a sequel, "A New Era," arrived in 2022. This 2025 film, "The Grand Finale," concludes a story spanning <Stat>15 years</Stat> counting from the television series.</p>
              <p className={P}>The upstairs-and-downstairs saga of the aristocratic Crawley family and the servants led by Mr. Carson reaches its grand conclusion with this film. The lineage of "pride in service," handed down through decades of change, arrives at a fitting close.</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-thenumbers.jpg"
              alt="Screenshot of The Numbers page listing the release data and box office performance of the film"
              media="The Numbers"
              url="https://www.the-numbers.com/movie/Downton-Abbey-The-Grand-Finale-(2025-United-Kingdom)"
            />

            {/* 07: International Response and Cultural Value (existing body, unchanged) */}
            <SectionHeading id="s-global" en="Global Response">International Response and Cultural Value</SectionHeading>
            <div className="space-y-6">
              <p className={P}>This film was simultaneously released not only in the UK but also in the United States, Canada, Australia, Japan, and other countries worldwide, receiving high acclaim in all countries. The international spread of understanding and respect for the butler profession is particularly culturally significant.</p>
              <p className={P}>Critics highly praised it as "a masterpiece brilliantly depicting the dignity and expertise of the butler profession" and "a work reminding us of the essence of service that modern society is losing," positioning it as a work with cultural value beyond mere entertainment.</p>
            </div>
          </article>

          {/* Sidebar (lg+, sticky). Hidden on mobile; sources/related fall below the body on mobile */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Release</dt><dd className="text-[#333] mt-0.5">September 12, 2025 (U.S.)</dd></div>
                  <div><dt className="text-[#8f7a56]">Distributor</dt><dd className="text-[#333] mt-0.5">Focus Features</dd></div>
                  <div><dt className="text-[#8f7a56]">Category</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                  <div><dt className="text-[#8f7a56]">Worldwide Gross</dt><dd className="text-[#333] mt-0.5">approx. $103.5M</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Association comment */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      {/* Source */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      {/* Related */}
      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
